import { computed, ref } from 'vue'

import isSSR from '@/_base/isSSR'
import { changelog } from '@/config/changelog'
import type { ChangelogEntry } from '@/config/changelog'

const LAST_SEEN_KEY = '@ME:changelog_last_seen'

/**
 * Compara duas versoes semver ('1.10.268').
 * Retorna > 0 se `a` for mais nova, < 0 se mais antiga, 0 se iguais.
 */
function compareVersions(a: string, b: string): number {
  // parseInt (não Number) para tolerar sufixos tipo '1.10.0-beta' sem virar NaN;
  // `|| 0` cobre segmentos vazios/ausentes.
  const pa = a.split('.').map((seg) => Number.parseInt(seg, 10) || 0)
  const pb = b.split('.').map((seg) => Number.parseInt(seg, 10) || 0)

  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (diff !== 0) return diff
  }

  return 0
}

// Estado singleton em module scope: Footer, painel e pagina compartilham a mesma instancia.
const entries = ref<ChangelogEntry[]>(
  [...changelog].sort((a, b) => compareVersions(b.version, a.version))
)
const isPanelOpen = ref(false)
const lastSeenVersion = ref<string>(isSSR ? '' : (localStorage.getItem(LAST_SEEN_KEY) ?? ''))

const latestVersion = computed(() => entries.value[0]?.version ?? '')

const hasUnread = computed(() => {
  if (!latestVersion.value) return false
  return compareVersions(latestVersion.value, lastSeenVersion.value) > 0
})

function markAllAsSeen() {
  if (!latestVersion.value) return
  if (!isSSR) localStorage.setItem(LAST_SEEN_KEY, latestVersion.value)
  lastSeenVersion.value = latestVersion.value
}

function openPanel() {
  isPanelOpen.value = true
  markAllAsSeen()
}

function closePanel() {
  isPanelOpen.value = false
}

export function useChangelog() {
  return {
    entries,
    latestVersion,
    hasUnread,
    isPanelOpen,
    openPanel,
    closePanel,
    markAllAsSeen
  }
}
